import clsx from 'clsx';
import { ca } from 'date-fns/locale';
import { format } from 'date-fns';
import { useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import Select from 'react-select';
import { toAbsoluteUrl, valueHandler } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import StatusBadge from '../../../../../modules/components/badge/StatusBadge';
import CopyTextButton from '../../../../../modules/components/copyText/CopyTextButton';
import { getSocialMediaFromLink, SocialMedia } from '../../../../../modules/components/socialMedia/mappings';
import SocialMediaIcon from '../../../../../modules/components/socialMedia/SocialMediaIcon';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { getVpnLabel, SiRequestStatus, SiService } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import VisitHistoryModal from '../modal/VisitHistoryModal';
import RowHeaderTable, { RowHeaderRow } from '../table/RowHeaderTable';
import {
  getFieldUpdate,
  getImagePathByFilename,
  parseSiRequestStatus,
  SiRequestUser,
} from '../utils';
import BasicInformation from './BasicInformation';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  data?: any;
  socialOutboundResults: any;
  domainResults: any;
  screenshots: any;
  webResults: any;
  categoryResults: any;
  isEditable?: boolean;
  isSaving?: boolean;
  isSiAiNewData?: boolean;
  handleExpandImage: (imagePath: string) => void;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSave?: UsePageDataManagerResult['saveService'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

export default function Summary({ data,
  socialOutboundResults,
  domainResults,
  screenshots,
  webResults,
  categoryResults,
  isEditable,
  isSaving,
  isSiAiNewData,
  handleExpandImage,
  onUpdate,
  onSave,
  onReset,
  checkDirty,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const { setToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);

  const t = (id: string, values?: Record<string, string | number>) =>
    intl.formatMessage({ id: `SI_SUMMARY.${id}` }, values);

  const isDirty = checkDirty && checkDirty(SiService.CATEGORIZE_SERVICE);
  const requestId = data?.results?.request_id;

  const emDash = '—';
  const previewSocialMediaLimit = 10;
  const blockedTitlePatterns = [
    // Common
    'Access Denied',
    'Access to this page has been denied',
    'Forbidden',
    '403 Forbidden',
    'Bot Detected',
    'Automation Detected',
    'Security Challenge',
    'Please Wait...',
    'Checking your browser',
    'Verification Required',
    'Unavailable',
    'Restricted Access',
    'Blocked',

    // Cloudflare
    'Just a moment...',
    'Attention Required!',
    'Security Check',

    // AWS
    'AccessDenied',

    // Akamai
    'Akamai Bot Manager',

    // Other Service Providers
    'Distil Captcha',
    'PerimeterX',
    'Imperva',
    'Incapsula',
  ];

  const mainScreenshotFilename =
    domainResults && screenshots
      ? screenshots?.find(
        (item) => item?.vpn === domainResults?.result?.geo_info?.selected
      )?.file_path ?? screenshots?.[0]?.file_path
      : '';

  const uniqueSocialMedia: {
    socialMedia: SocialMedia;
    link: string;
  }[] = [];
  if (socialOutboundResults && socialOutboundResults?.result?.fqdn_group?.count > 0) {
    const pastSocialMedia = new Set();
    for (const item of socialOutboundResults?.detail) {
      if (uniqueSocialMedia.length >= previewSocialMediaLimit) break;
      const socialMedia = getSocialMediaFromLink(item?.found_link);
      if (!pastSocialMedia.has(socialMedia)) {
        pastSocialMedia.add(socialMedia);
        uniqueSocialMedia.push({
          socialMedia,
          link: item?.found_link,
        });
      }
    }
  }

  const getDurationText = (duration?: { years?: number; months?: number; days?: number }) => {
    if (!duration) return emDash;

    const parts: string[] = [];
    if (duration?.years) parts.push(`${duration.years} ${t('YEARS')}`);
    if (duration?.months) parts.push(`${duration.months} ${t('MONTHS')}`);
    if (duration?.days) parts.push(`${duration.days} ${t('DAYS')}`);

    return parts.length > 0 ? <span>{parts.join(' ')}</span> : emDash;
  };

  const headerOtherData: RowHeaderRow[] = [
    {
      key: 'socialMedia',
      header: t('SOCIAL_MEDIA'),
      value:
        socialOutboundResults?.result?.fqdn_group?.count > 0 ? (
          <div className='d-flex flex-wrap gap-2 align-items-center'>
            {uniqueSocialMedia.slice(0, previewSocialMediaLimit).map((item, idx) => (
              <a key={idx} href={item?.link} target='_blank' rel='noopener noreferrer'>
                <SocialMediaIcon
                  socialMedia={item?.socialMedia}
                  className='w-20px h-20px bg-white'
                />
              </a>
            ))}
            {socialOutboundResults?.detail?.length > uniqueSocialMedia.length && (
              <a
                href='#social-outbound-content'
                className='badge badge-light rounded-pill text-primary'
              >
                +{socialOutboundResults?.detail?.length - uniqueSocialMedia.length}
              </a>
            )}
          </div>
        ) : (
          emDash
        ),
    },
    {
      key: 'domainAge',
      header: t('DOMAIN_AGE'),
      value:
        Object.keys(domainResults?.result?.domain_info?.age ?? {}).length > 0 ? (
          getDurationText(domainResults?.result?.domain_info?.age)
        ) : (
          emDash
        ),
    },
    {
      key: 'sslValidityPeriod',
      header: t('SSL_VALIDITY_PERIOD'),
      value:
        domainResults?.result?.security_info?.ssl_info?.ssl_validity_period &&
          Object.keys(domainResults?.result?.security_info?.ssl_info?.ssl_validity_period).length >
          0 ? (
          <span
            className={
              domainResults?.result?.security_info?.ssl_info?.ssl_validity_period?.years
                ? ''
                : 'text-danger'
            }
          >
            {getDurationText(domainResults?.result?.security_info?.ssl_info?.ssl_validity_period)}
          </span>
        ) : (
          emDash
        ),
    },
    {
      key: 'siteAvailability',
      header: t('SITE_AVAILABILITY'),
      value: domainResults?.result?.domain_info?.site_found ? t('AVAILABLE') : t('NOT_FOUND'),
    },
    {
      key: 'companyName',
      header: t('DETECTED_COMPANY_NAME'),
      value:
        webResults?.result?.company_name?.length > 0
          ? webResults?.result?.company_name?.[0]
          : emDash,
    },
    ...isSiAiNewData !== true ? [{
      key: 'checkoutIndicators',
      header: (
        <>
          {t('CHECKOUT_INDICATORS')}
        </>
      ),
      value: <>
        {isEditing ? <div>
          <div className='d-flex align-items-center mb-1' style={{ width: '350px' }}>
            <i className='bi bi-cart3 me-2' style={{ fontSize: '20px', color: 'grey', width: '24px' }} title={t('SHOPPING_CART')} />
            <Select
              options={[
                { label: 'Have Shopping Cart', value: true },
                { label: 'Do Not Have Shopping Cart', value: false },
              ]}
              placeholder=''
              noOptionsMessage={() =>
                intl.formatMessage({ id: 'CLIENTS.SELECTION_DESCRIPTION' })
              }
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderRadius: 0,
                  boxShadow: 'none',
                  width: '320px',
                }),
                singleValue: (baseStyles) => ({
                  ...baseStyles,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }),
              }}
              classNames={{
                control: (state) =>
                  clsx(
                    'h-100 border-gray-300 rounded-end',
                    state.isFocused ? 'border-gray-400' : ''
                  ),
              }}
              value={
                categoryResults?.result?.image_to_text_checkout?.shopping_cart_exist
                  ? { label: 'Have Shopping Cart', value: true }
                  : { label: 'Do Not Have Shopping Cart', value: false }
              }
              onChange={(option) => {
                if (!option) return;

                onUpdate(SiService.CATEGORIZE_SERVICE, (draft) => {
                  draft.result.image_to_text_checkout.shopping_cart_exist = option.value;
                });
              }}
              menuPortalTarget={document.body}
            />
          </div>
          <div className='d-flex align-items-center' style={{ width: '350px' }}>
            <i className='bi bi-credit-card me-2' style={{ fontSize: '22px', color: 'grey', width: '24px' }} title={t('PAYMENT_ICON')} />
            <Select
              options={[
                { label: 'Have Payment Icon', value: true },
                { label: 'Do Not Have Payment Icon', value: false },
              ]}
              placeholder=''
              noOptionsMessage={() =>
                intl.formatMessage({ id: 'CLIENTS.SELECTION_DESCRIPTION' })
              }
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderRadius: 0,
                  boxShadow: 'none',
                  width: '320px',
                }),
                singleValue: (baseStyles) => ({
                  ...baseStyles,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }),
              }}
              classNames={{
                control: (state) =>
                  clsx(
                    'h-100 border-gray-300 rounded-end',
                    state.isFocused ? 'border-gray-400' : ''
                  ),
              }}
              value={
                categoryResults?.result?.image_to_text_checkout?.payment_icon_exist
                  ? { label: 'Have Payment Icon', value: true }
                  : { label: 'Do Not Have Payment Icon', value: false }
              }
              onChange={(option) => {
                if (!option) return;

                onUpdate(SiService.CATEGORIZE_SERVICE, (draft) => {
                  draft.result.image_to_text_checkout.payment_icon_exist = option.value;
                });
              }}
              menuPortalTarget={document.body}
            />
          </div>
        </div> : <div>
          <div className='d-flex align-items-start justify-content-between gap-3'>
            {categoryResults?.result?.image_to_text_checkout?.shopping_cart_exist || categoryResults?.result?.image_to_text_checkout?.payment_icon_exist ? <div className='d-flex flex-wrap gap-4'>
              {categoryResults?.result?.image_to_text_checkout?.shopping_cart_exist && <i
                className={clsx('bi bi-cart3')}
                style={{ fontSize: '20px', color: 'grey' }}
                title={t('SHOPPING_CART')}
              />}
              {categoryResults?.result?.image_to_text_checkout?.payment_icon_exist && <i
                className={clsx('bi bi-credit-card')}
                style={{ fontSize: '22px', color: 'grey' }}
                title={t('PAYMENT_ICON')}
              />}
            </div> : emDash}
          </div>
        </div>}
      </>
    }] : [],
  ];

  const handleReset = () => {
    onReset(SiService.CATEGORIZE_SERVICE);
  };

  const handleSave = async () => {
    await onSave(SiService.CATEGORIZE_SERVICE);
    setIsEditing(false);

    // Scroll to top of card
    const element = document.getElementById('summary');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  const handlePrintToPdf = () => {
    const originalTitle = document.title;

    const formatDateHongKong = (dateValue?: string) => {
      if (!dateValue) {
        return '';
      }

      const requestDate = new Date(dateValue);
      if (Number.isNaN(requestDate.getTime())) {
        return dateValue;
      }

      return formatInTimeZone(requestDate, 'Asia/Hong_Kong', 'yyyy-MM-dd');
    };

    const toFqdn = (value?: string) => {
      const trimmedValue = (value ?? '').trim();
      if (!trimmedValue) {
        return '';
      }

      const parseCandidate =
        /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `http://${trimmedValue}`;
      try {
        const parsedUrl = new URL(parseCandidate);
        return parsedUrl.hostname || trimmedValue;
      } catch {
        return trimmedValue
          .replace(/^https?:\/\//i, '')
          .split(/[/?#]/)[0]
          .split(':')[0]
          .trim();
      }
    };

    const domainQueryValue = toFqdn(data?.results?.queries?.[0]?.query?.value ?? '');
    const reportDay = formatDateHongKong(data?.results?.report_date);
    const customTitle = `Austreme Report - ${domainQueryValue} - ${reportDay}`;

    const restoreTitle = () => {
      document.title = originalTitle;
      window.removeEventListener('afterprint', restoreTitle);
    };

    document.title = customTitle;
    window.addEventListener('afterprint', restoreTitle);
    window.print();

    setTimeout(restoreTitle, 0);
  };

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        headerToolbar={
          (isEditable || data?.results?.status === SiRequestStatus.REVIEWED) && (
            <>
              {isEditable && isSiAiNewData !== true && (isEditing ? (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    if (isDirty) {
                      handleReset();
                    }
                    setIsEditing(false);
                  }}
                  className={clsx('btn btn-sm btn-secondary')}
                  title='Cancel Edit'
                >
                  Cancel
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.currentTarget.blur();
                    setIsEditing(true);
                  }}
                  className={clsx('btn btn-sm btn-primary')}
                  title='Edit'
                >
                  Edit
                </button>
              ))}
              {data?.results?.status === SiRequestStatus.REVIEWED && <button
                onClick={(e) => {
                  e.currentTarget.blur();
                  handlePrintToPdf();
                }}
                type='button'
                className={clsx('btn btn-sm btn-primary print-d-none')}
                title={t('PRINT_TO_PDF')}
              >
                <i className="bi bi-printer p-0"></i>
              </button>}
            </>
          )
        }
        isEditable={isEditable && isEditing}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={handleSave}
        onReset={handleReset}
      >
        <div className='card mb-5 mb-xl-10'>
          <div className='card-body p-9 pt-5'>
            {/* Merchant-given Info */}
            <div className='text-dark fw-bolder fs-6 mx-2 mb-2'>{t('CLIENT_INPUT_INFO')}</div>
            <BasicInformation data={data?.results} isEditable={isEditable} />

            {/* Website Info */}
            <div className='row g-5 g-md-9'>
              {/* Header left side: screenshot + title, desc, keyword */}
              <div className='col-12 col-xl-6 d-flex flex-column gap-6'>
                <div
                  className='position-relative shadow-sm rounded overflow-hidden bg-gray-100'
                  style={{ height: 'clamp(220px, 32vw, 350px)' }}
                >
                  {mainScreenshotFilename ? (
                    <>
                      <button
                        type='button'
                        className='btn btn-dark bg-dark bg-opacity-75 position-absolute end-0 mt-2 me-2 py-1 px-2'
                        onClick={() =>
                          handleExpandImage(getImagePathByFilename(mainScreenshotFilename))
                        }
                        title={t('VIEW_IMAGE')}
                      >
                        <i className='bi bi-arrows-angle-expand p-0 fs-6'></i>
                      </button>
                      <img
                        src={getImagePathByFilename(mainScreenshotFilename)}
                        className='w-100 h-100 rounded'
                        style={{ objectFit: 'contain' }}
                      />
                    </>
                  ) : (
                    // No image placeholder
                    <div className='w-100 h-100 rounded bg-gray-200 d-flex align-items-center justify-content-center ratio ratio-16x9'>
                      <img src={toAbsoluteUrl('/media/icons/duotune/general/gen006.svg')} />
                    </div>
                  )}

                  {(domainResults?.result?.domain_info?.title ||
                    domainResults?.result?.domain_info?.description ||
                    domainResults?.result?.domain_info?.keywords) && (
                      <div className='position-absolute bottom-0 w-100'>
                        <div className='position-relative'>
                          {/* Faded edge opaque BG overlay */}
                          <div
                            className='position-absolute bottom-0 w-100 rounded-bottom d-print-none'
                            style={{
                              top: '-10%', // padding
                              backdropFilter:
                                'blur(34px) brightness(0.95) saturate(1.6) contrast(1.1)',
                              WebkitBackdropFilter:
                                'blur(34px) brightness(0.95) saturate(1.6) contrast(1.1)',
                              maskImage:
                                'linear-gradient(transparent, rgba(0, 0, 0, .75) 30%, #000 70%)',
                              WebkitMask:
                                'linear-gradient(transparent, rgba(0, 0, 0, .75) 30%, #000 75%)',
                              background: blockedTitlePatterns.some(
                                (pattern) =>
                                  pattern.toLocaleLowerCase() ===
                                  domainResults?.result?.domain_info?.title.toLocaleLowerCase()
                              )
                                ? 'rgba(255,0,0,.68)'
                                : 'rgba(0,0,0,.78)',
                              zIndex: 1,
                            }}
                          ></div>
                          {/* Solid overlay for print mode */}
                          <div
                            className='position-absolute top-0 bottom-0 w-100 rounded-bottom d-none d-print-block'
                            style={{
                              background: blockedTitlePatterns.some(
                                (pattern) =>
                                  pattern.toLocaleLowerCase() ===
                                  domainResults?.result?.domain_info?.title.toLocaleLowerCase()
                              )
                                ? 'rgba(250,0,0,.68)'
                                : 'rgba(0,0,0,.78)',
                              zIndex: 1,
                            }}
                          ></div>
                          {/* Content */}
                          <div
                            className='position-relative d-flex flex-column gap-1 p-4'
                            style={{ zIndex: 2 }}
                          >
                            {domainResults?.result?.domain_info?.title && (
                              <h5
                                className={clsx('m-0 fs-2 text-truncate text-light')}
                                title={domainResults?.result?.domain_info?.title}
                              >
                                {domainResults?.result?.domain_info?.title}
                              </h5>
                            )}
                            {domainResults?.result?.domain_info?.description && (
                              <div className='opacity-75 text-light text-truncate'>
                                {domainResults?.result?.domain_info?.description}
                              </div>
                            )}
                            {domainResults?.result?.domain_info?.keywords && (
                              <div className='text-truncate opacity-75 text-light fst-italic'>
                                <span className='fw-bold'>{t('KEYWORDS')}: </span>
                                {domainResults?.result?.domain_info?.keywords}
                              </div>
                            )}
                            <div className='d-flex justify-content-between align-items-end'>
                              <a
                                href='#domain-content'
                                className='text-light fs-8 opacity-75 fw-bold text-hover-light-primary text-decoration-underline'
                              >
                                {t('READ_MORE')}
                              </a>
                              {mainScreenshotFilename && (
                                <div className='text-light fs-8 opacity-75 fw-bold'>
                                  {getVpnLabel(
                                    domainResults?.result?.geo_info?.selected,
                                    intl.formatMessage
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>

              {/* Header right side: other info */}
              <div className='col-12 col-xl-6'>
                <div className='d-flex flex-column gap-6'>
                  <RowHeaderTable rows={headerOtherData} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </EditableCardContainer>

      {/* <VisitHistoryModal
        show={showVisitHistoryModal}
        requestId={requestId}
        onHide={() => setShowVisitHistoryModal(false)}
      /> */}
    </>
  )
}
